<template>
    <div class="rtb-campaign-form">
        <div class="rtb-campaign-form__inner">
            <RtbFormHeader
                :title="pageTitle"
                help="Выберите пакет экранов — состав и цена рассчитываются автоматически. Ставки и бюджет задаёт система по цене пакета за сутки."
                :is-read-only="isReadOnly"
                :is-draft="isCurrentlyDraft"
                :display-status="displayStatus"
                :display-status-label="displayStatusLabel"
                :status-hint="statusHint"
                :will-remoderate="willRemoderate"
            />

            <form class="form" @submit.prevent="submit">
                <div v-if="!isEditMode && canWrite" class="rtb-form-import">
                    <button
                        type="button"
                        class="btn btn--secondary"
                        :disabled="importingSettings"
                        @click="triggerSettingsImport"
                    >
                        {{ importingSettings ? 'Загрузка…' : 'Загрузить настройки' }}
                    </button>
                    <input
                        ref="settingsFileInput"
                        type="file"
                        accept=".json,application/json"
                        class="visually-hidden"
                        @change="onSettingsFileSelected"
                    />
                </div>

                <RtbFormIdentity
                    :form="form"
                    :errors="errors"
                    :advertisers="advertisers"
                    :brands="filteredBrands"
                    :disabled="!canWrite"
                    :is-edit="isEditMode"
                    :is-admin="isAdmin"
                    @advertiser-change="onAdvertiserChange"
                />

                <RtbFormPeriod
                    :form="form"
                    :errors="errors"
                    :disabled="!canWrite"
                    :is-edit="isEditMode"
                    :today="today"
                    :start-locked="isStartDateLocked"
                    :end-min="endDateMin"
                >
                    <template #schedule>
                        <RtbScheduleGrid
                            :model-value="campaignSchedule"
                            :start-date="form.start_date"
                            :end-date="form.end_date"
                            :editable="canWrite"
                            @update:modelValue="campaignSchedule = $event"
                        />
                    </template>
                </RtbFormPeriod>

                <RtbFormPackage
                    :model-value="form.screen_package_id"
                    :packages="packages"
                    :selected-package="selectedPackage"
                    :can-change="!isEditMode || canAttachPackage"
                    :disabled="!canWrite"
                    :errors="errors"
                    :price-loading="priceLoading"
                    :price-preview="pricePreview"
                    :price-error="priceError"
                    :format-money="formatMoney"
                    @update:modelValue="id => { form.screen_package_id = id; onPackageChange(); }"
                />

                <RtbFormCreatives
                    v-model="selectedCreativeIds"
                    :creatives="allCreatives"
                    :errors="errors"
                    :disabled="!canWrite"
                    :show-readiness-hint="showCreativeReadinessHint"
                    :is-selected="isCreativeSelected"
                    :is-paused="isCreativePausedInCampaign"
                    :is-ready="isCreativeReadyForImpressions"
                    :readiness-tooltip="getCreativeReadinessTooltip"
                />

                <RtbFormScreens
                    title="Экраны пакета"
                    hint="Состав пакета. Выбор другого пакета на карте доступен до первой привязки."
                    empty-text="Выберите пакет с экранами."
                    :selected-gids="selectedFrameGids"
                    :exporting="exportingScreens"
                    :show-map="packageFrames.length > 0"
                    :errors="errors"
                    @export-screens="exportSelectedScreens"
                >
                    <GoogleMap
                        :frames="packageFrames"
                        v-model:selectedFrameGids="selectedFrameGids"
                        sidebar-mode="packages"
                        :packages="packages"
                        :selected-package-id="effectivePackageId"
                        :disable-price-check="true"
                        :hide-required-star="true"
                        :selected-creatives="selectedCreativeObjects"
                        :allow-multi-ssp="true"
                        :read-only="!canWrite"
                        :can-manage-test-frames="isAdmin"
                        @select-package="onPackageMapSelect"
                    />
                </RtbFormScreens>

                <RtbFormLimits
                    :form="form"
                    :errors="errors"
                    :disabled="!canWrite"
                    :show-budget="false"
                />

                <AudienceDataOption
                    :enabled="audienceEnabled"
                    :locked="audienceLocked"
                    :has-beeline="hasBeelineSelected"
                    :can-enable="!isEditMode || isCurrentlyDraft"
                    :can-write="canWrite"
                    :markup-percent="audienceMarkupPercent"
                    :offer-url="audienceOfferUrl"
                    :payable-hint="packagePayableHint"
                    :preview="audiencePreview"
                    @update:enabled="onAudienceEnabled"
                />

                <RtbFormFooter
                    :can-write="canWrite"
                    :can-submit="isValid && !!pricePreview && !priceError"
                    :can-draft="canSaveDraft"
                    :show-draft="!isEditMode || isCurrentlyDraft"
                    :is-edit="isEditMode"
                    :busy="saving"
                    :as-draft="savingAsDraft"
                    :exporting="exportingSettings"
                    :general-error="generalError"
                    @draft="submitDraft"
                    @export-settings="exportSettings"
                    @cancel="confirmLeave(cancelUrl)"
                />
            </form>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import axios from 'axios';
import GoogleMap from '../../common/GoogleMap.vue';
import AudienceDataOption from './AudienceDataOption.vue';
import RtbScheduleGrid from './RtbScheduleGrid.vue';
import RtbFormHeader from './form/RtbFormHeader.vue';
import RtbFormIdentity from './form/RtbFormIdentity.vue';
import RtbFormPeriod from './form/RtbFormPeriod.vue';
import RtbFormPackage from './form/RtbFormPackage.vue';
import RtbFormCreatives from './form/RtbFormCreatives.vue';
import RtbFormScreens from './form/RtbFormScreens.vue';
import RtbFormLimits from './form/RtbFormLimits.vue';
import RtbFormFooter from './form/RtbFormFooter.vue';
import { useRtbCampaignForm } from '../../../composables/useRtbCampaignForm';
import { useToast } from '../../../composables/useToast';
import { RTB_CAMPAIGN_TYPE } from '../../../constants/rtbCampaigns';
import { extractErrorMessage } from '../../../utils/httpErrors';
import { exportScreensExcel } from '../../../utils/exportScreensExcel';

const props = defineProps({
    campaign: { type: Object, default: null },
    userData: { type: Object, default: () => ({}) },
    advertisers: { type: Array, default: () => [] },
    frames: { type: Array, default: () => [] },
    availableCreatives: { type: Array, default: () => [] },
    packages: { type: Array, default: () => [] },
    campaignCreatives: { type: Array, default: () => [] },
    initialSelectedFrameGids: { type: Array, default: () => [] },
    audienceConfig: { type: Object, default: () => ({ markup_percent: 10, offer_url: '#' }) },
    type: { type: String, default: RTB_CAMPAIGN_TYPE.PACKAGE },
});

const toast = useToast();
const selectedFrameGids = ref([]);
const exportingScreens = ref(false);
const priceLoading = ref(false);
const pricePreview = ref(null);
const priceError = ref('');

const formatMoney = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return '0 ₸';
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'KZT', maximumFractionDigits: 0 }).format(n);
};

const effectivePackageId = computed(() => {
    if (props.campaign?.id && (props.campaign.screen_package_id || props.campaign.screen_package?.id)) {
        return props.campaign.screen_package_id || props.campaign.screen_package?.id;
    }
    return form.screen_package_id;
});

const canAttachPackage = computed(() => Boolean(
    props.campaign?.id && !(props.campaign.screen_package_id || props.campaign.screen_package?.id),
));

const selectedPackage = computed(() => props.packages.find(pkg => pkg.id === effectivePackageId.value) || null);

const packageFrames = computed(() => {
    const pkg = selectedPackage.value;
    if (!pkg?.frames?.length) {
        return props.frames.filter(frame => selectedFrameGids.value.includes(String(frame.gid)));
    }
    const gids = new Set(pkg.frames.map(frame => String(frame.gid)));
    return props.frames.filter(frame => gids.has(String(frame.gid)));
});

const extraPayload = () => {
    const extra = {};
    if (!props.campaign?.id || canAttachPackage.value) {
        extra.screen_package_id = form.screen_package_id || null;
    }
    return extra;
};

const extraValidate = (asDraft) => {
    if (asDraft) return {};
    const result = {};
    if (!pricePreview.value) result.screen_package_id = priceError.value || 'Не удалось рассчитать цену пакета';
    if (selectedFrameGids.value.length === 0) result.frame_gids = 'В пакете нет экранов';
    return result;
};

const {
    form,
    errors,
    generalError,
    saving,
    savingAsDraft,
    exportingSettings,
    importingSettings,
    isCurrentlyDraft,
    selectedCreativeIds,
    campaignSchedule,
    audienceEnabled,
    audienceLocked,
    audiencePreview,
    settingsFileInput,
    today,
    canWrite,
    isReadOnly,
    isEditMode,
    isAdmin,
    pageTitle,
    cancelUrl,
    isStartDateLocked,
    endDateMin,
    filteredBrands,
    allCreatives,
    selectedCreativeObjects,
    showCreativeReadinessHint,
    hasBeelineSelected,
    audienceMarkupPercent,
    audienceOfferUrl,
    displayStatus,
    displayStatusLabel,
    statusHint,
    willRemoderate,
    canSaveDraft,
    isValid,
    submit,
    submitDraft,
    exportSettings,
    triggerSettingsImport,
    onSettingsFileSelected,
    onAdvertiserChange,
    onAudienceEnabled,
    isCreativeSelected,
    isCreativePausedInCampaign,
    isCreativeReadyForImpressions,
    getCreativeReadinessTooltip,
    confirmLeave,
} = useRtbCampaignForm(props, {
    selectedFrameGids,
    extraPayload,
    extraValidate,
    onHydrated: () => {
        selectedFrameGids.value = (props.initialSelectedFrameGids || []).map(String);
    },
    onSettingsApplied: (payload) => {
        const warnings = [];
        if (!props.campaign?.id || canAttachPackage.value) {
            const packageIds = new Set((props.packages || []).map(pkg => Number(pkg.id)));
            if (payload.screen_package_id == null || payload.screen_package_id === '') {
                form.screen_package_id = null;
                selectedFrameGids.value = [];
            } else if (packageIds.has(Number(payload.screen_package_id))) {
                form.screen_package_id = Number(payload.screen_package_id);
                onPackageChange();
            } else {
                form.screen_package_id = null;
                warnings.push(`Пакет ${payload.screen_package_id} недоступен`);
            }
        }
        fetchPricePreview();
        return warnings;
    },
});

const packagePayableHint = computed(() => {
    const total = Number(pricePreview.value?.total) || 0;
    if (!audienceEnabled.value || total <= 0) return '';
    const payable = Math.round(total * (1 + audienceMarkupPercent.value / 100) * 100) / 100;
    return `К оплате с аудиторными данными: ${formatMoney(payable)}`;
});

const onPackageChange = () => {
    const pkg = selectedPackage.value;
    if (pkg?.frames?.length) {
        selectedFrameGids.value = pkg.frames.map(frame => String(frame.gid));
    }
    fetchPricePreview();
};

const onPackageMapSelect = (pkg) => {
    if ((!isEditMode.value || canAttachPackage.value) && pkg?.id) {
        form.screen_package_id = pkg.id;
        onPackageChange();
    }
};

const fetchPricePreview = async () => {
    if (!form.start_date || !form.end_date || !effectivePackageId.value) {
        pricePreview.value = null;
        return;
    }
    priceLoading.value = true;
    priceError.value = '';
    try {
        const payload = {
            screen_package_id: effectivePackageId.value,
            start_date: form.start_date,
            end_date: form.end_date,
            advertiser_id: form.advertiser_id,
            audience_data_enabled: audienceEnabled.value,
        };
        if (isEditMode.value && props.campaign?.id) payload.campaign_rtb_id = props.campaign.id;
        const { data } = await axios.post('/rtb/package-campaigns/price-preview', payload);
        if (data.valid) pricePreview.value = data.preview;
    } catch (error) {
        pricePreview.value = null;
        priceError.value = extractErrorMessage(error, 'Не удалось рассчитать цену');
        const field = error.response?.data?.errors;
        if (field) priceError.value = Object.values(field).flat().join(' ');
    } finally {
        priceLoading.value = false;
    }
};

const exportSelectedScreens = async () => {
    if (exportingScreens.value) return;
    exportingScreens.value = true;
    try {
        const result = await exportScreensExcel(selectedFrameGids.value, {
            campaignRtbId: props.campaign?.id || undefined,
        });
        if (!result.ok) toast.error(result.error);
    } catch {
        toast.error('Не удалось выгрузить экраны');
    } finally {
        exportingScreens.value = false;
    }
};

watch(() => [form.advertiser_id, form.start_date, form.end_date, audienceEnabled.value], fetchPricePreview);
</script>

<style lang="scss">
@use '../../../../scss/components/rtb-campaign-form';
</style>
